import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HardHat, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

/**
 * Mobile-first login for the field team app at /tecnicos/login.
 * After success, redirects to the technician's complete agenda.
 */
export default function TechLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin" || u.role === "operator") {
        // Admins can also enter as tech to review
        navigate("/tecnicos", { replace: true });
      } else if (u.role === "technician") {
        navigate("/tecnicos", { replace: true });
      } else {
        setError("Tu usuario no tiene acceso a la app de terreno.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tech-login" className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 py-10">
        <div className="max-w-sm mx-auto w-full">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-[#FF5A00] flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-[#FF5A00]">
                // App de terreno
              </div>
              <div className="font-tactical font-black uppercase tracking-tight text-2xl leading-none">
                All Active <span className="text-[#FF5A00]">Field</span>
              </div>
            </div>
          </div>

          <h1 className="font-tactical font-black uppercase text-4xl leading-[0.9] mb-3">
            Buenos días,<br />técnico.
          </h1>
          <p className="text-white/60 text-sm mb-8">
            Tu agenda y tus visitas, en un solo lugar.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="tech-login-form">
            <div>
              <label className="block font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="tech-email"
                  className="w-full pl-10 pr-4 py-3.5 bg-white/5 border border-white/15 focus:border-[#FF5A00] outline-none text-white text-base"
                  placeholder="tunombre@allactive.cl"
                />
              </div>
            </div>
            <div>
              <label className="block font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="tech-password"
                  className="w-full pl-10 pr-11 py-3.5 bg-white/5 border border-white/15 focus:border-[#FF5A00] outline-none text-white text-base"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="tech-toggle-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-[#FF5A00]"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="tech-login-error" role="alert" className="text-sm text-[#FF6B6B] bg-[#D50000]/10 border border-[#D50000]/40 px-4 py-3">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="tech-login-submit"
              className="group w-full bg-[#FF5A00] hover:bg-[#E04D00] active:bg-[#C24000] text-white font-mono-kpi text-sm uppercase tracking-[0.24em] py-4 flex items-center justify-between px-6 transition-colors disabled:opacity-60"
            >
              <span>{loading ? "Ingresando…" : "Entrar"}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
      <div className="text-center py-6 font-mono-kpi text-[10px] uppercase tracking-[0.24em] text-white/30">
        All Active · v1.0
      </div>
    </div>
  );
}
